import { useEffect, useRef, useState } from "react";

// JMMEDIAT-900 (spec 53) — how often a page that refreshes itself should poll, given whether anyone is
// looking at it. Production IIS logs (2026-09-11 → 10-06): the delivery page's three 10 s polls were ~90 %
// of all API server time, and 82 pages left open for more than 8 hours produced 78 % of that polling.
//
//   tab hidden (other tab, minimised, locked screen) → 0 (RTK Query: no polling)
//   visible, no mouse / key / scroll / touch for `idleAfterMs` → `idleMs`
//   otherwise → `activeMs`
//
// Coming back to the tab counts as activity. Pair it with `refetchOnFocus: true` on the query so the data
// refreshes the moment the tab is visible again (`setupListeners` is already wired in store.ts).

export const POLL_ACTIVE_MS = 10000;
export const POLL_IDLE_MS = 120000;
export const POLL_IDLE_AFTER_MS = 15 * 60000;

const IDLE_CHECK_MS = 30000;
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "scroll", "touchstart"];
// capture: `scroll` does not bubble, and the delivery page scrolls an inner container, not the window.
const LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };

const isHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";

type PollingOptions = {
  activeMs?: number;
  idleMs?: number;
  idleAfterMs?: number;
};

export const usePollingInterval = ({
  activeMs = POLL_ACTIVE_MS,
  idleMs = POLL_IDLE_MS,
  idleAfterMs = POLL_IDLE_AFTER_MS,
}: PollingOptions = {}): number => {
  const [hidden, setHidden] = useState(isHidden);
  const [idle, setIdle] = useState(false);
  // Activity only touches refs — a mouse move must not re-render the page. State changes only on the
  // idle ↔ active and hidden ↔ visible transitions.
  const lastActivity = useRef(Date.now());
  const idleRef = useRef(false);

  useEffect(() => {
    const markActive = () => {
      lastActivity.current = Date.now();
      if (idleRef.current) {
        idleRef.current = false;
        setIdle(false);
      }
    };

    const onVisibilityChange = () => {
      const nowHidden = isHidden();
      setHidden(nowHidden);
      if (!nowHidden) {
        markActive();
      }
    };

    const idleCheck = window.setInterval(() => {
      if (!idleRef.current && Date.now() - lastActivity.current >= idleAfterMs) {
        idleRef.current = true;
        setIdle(true);
      }
    }, Math.min(IDLE_CHECK_MS, idleAfterMs));

    document.addEventListener("visibilitychange", onVisibilityChange);
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, markActive, LISTENER_OPTIONS));

    return () => {
      window.clearInterval(idleCheck);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, markActive, LISTENER_OPTIONS));
    };
  }, [idleAfterMs]);

  if (hidden) {
    return 0;
  }

  return idle ? idleMs : activeMs;
};
